import { create } from "zustand";
import {
  content_tile_config,
  dashboard_element,
  display_config,
  edit_guide_tile_config,
  guide_tile_config,
} from "@/lib/types";

/**
 * Where the next gauge picked from the header search should be inserted. Set
 * by the positional "Add gauge here…" affordances and consumed by the search
 * result click handler. While non-null, the header search bar is focused so
 * picks land at this top-level index instead of the default prepend.
 */
export type InsertionTarget = { kind: "topLevel"; index: number };

/**
 * Normalizes a dashboard element to ensure display IDs are always objects.
 * Converts plain string display IDs to display_config objects immediately on load,
 * so downstream code can assume a consistent shape without repeated type checks.
 */
function normalizeDisplayConfig(element: string | display_config): display_config {
  if (typeof element === "string") {
    return { display_id: element, view_name: "rawlevel" };
  }
  return element;
}

/**
 * Normalizes all dashboard elements when loading from the database.
 * - Converts string display IDs to display_config objects
 * - Normalizes displayConfigs within guide tiles
 */
function normalizeDashboardElements(elements: dashboard_element[]): dashboard_element[] {
  return elements.map((element) => {
    // String display IDs become display_config objects
    if (typeof element === "string") {
      return normalizeDisplayConfig(element);
    }

    // Guide tiles: normalize their displayConfigs array, preserve contentTiles
    if (typeof element === "object" && "headerTitle" in element) {
      const guideTile = element as guide_tile_config;
      return {
        ...guideTile,
        displayConfigs: (guideTile.displayConfigs || []).map(normalizeDisplayConfig),
        contentTiles: guideTile.contentTiles || [],
      };
    }

    // display_config objects pass through as-is
    return element;
  });
}

type SetStateArg<T> = T | ((prev: T) => T);

type DashboardEditorState = {
  isEditMode: boolean;
  dashboardElements: dashboard_element[];
  originalDashboardElements: dashboard_element[];
  currentDashboardDisplayIds: string[];
  guideTiles: edit_guide_tile_config[];
  selectedTiles: string[];
  editingGuideTileIds: string[];
  groupingDropTargetId: string | null;
  insertionTarget: InsertionTarget | null;
  history: dashboard_element[][];
  historyIndex: number;
  // The dashboard's name differs from its saved one. The name lives in the
  // dashboard component, so it reports here for hasUnsavedDashboardEdits.
  nameEdited: boolean;
  setEditMode: (enabled: boolean) => void;
  setNameEdited: (edited: boolean) => void;
  setGroupingDropTargetId: (id: string | null) => void;
  hydrateDashboardElements: (elements: dashboard_element[]) => void;
  setDashboardElements: (
    updater: SetStateArg<dashboard_element[]>,
    options?: { recordHistory?: boolean }
  ) => void;
  resetDashboardElements: () => void;
  commitDashboardElements: () => void;
  setGuideTiles: (updater: SetStateArg<edit_guide_tile_config[]>) => void;
  reindexGuideTiles: () => void;
  reorderGuideTilesByElements: (elements: dashboard_element[]) => void;
  setCurrentDashboardDisplayIds: (updater: SetStateArg<string[]>) => void;
  addDisplayIdToCurrentDashboard: (id: string) => void;
  setInsertionTarget: (target: InsertionTarget | null) => void;
  insertGaugeAtTarget: (
    displayId: string
  ) => "inserted" | "duplicate" | "no-target";
  insertContentTileAt: (tile: content_tile_config, index: number) => void;
  toggleTileSelection: (id: string) => void;
  setTileSelection: (ids: string[]) => void;
  clearTileSelection: () => void;
  setGuideTileEditingState: (guideTileId: string, isEditing: boolean) => void;
  clearGuideTileEditingState: () => void;
  undo: () => void;
  redo: () => void;
  resetEditorState: () => void;
  updateElementConfig: (index: number, config: dashboard_element) => void;
};

const initialState = {
  isEditMode: false,
  dashboardElements: [] as dashboard_element[],
  originalDashboardElements: [] as dashboard_element[],
  currentDashboardDisplayIds: [] as string[],
  guideTiles: [] as edit_guide_tile_config[],
  selectedTiles: [] as string[],
  editingGuideTileIds: [] as string[],
  groupingDropTargetId: null,
  insertionTarget: null as InsertionTarget | null,
  history: [] as dashboard_element[][],
  historyIndex: -1,
  nameEdited: false,
};

export const useDashboardEditorStore = create<DashboardEditorState>(
  (set, get) => ({
    ...initialState,
    setGroupingDropTargetId: (id) => {
      set((state) => ({ ...state, groupingDropTargetId: id }));
    },
    setEditMode: (enabled) => {
      set((state) => ({
        ...state,
        isEditMode: enabled,
        groupingDropTargetId: null, // Always clear grouping drop target when mode changes
        insertionTarget: null, // Pending insert anchors don't survive mode changes
        ...(enabled
          ? {}
          : {
              nameEdited: false,
              currentDashboardDisplayIds: [],
              guideTiles: [],
              selectedTiles: [],
              editingGuideTileIds: [],
            }),
      }));
    },
    setNameEdited: (edited) => {
      set((state) => (state.nameEdited === edited ? state : { ...state, nameEdited: edited }));
    },
    hydrateDashboardElements: (elements) => {
      // Normalize elements immediately: convert string display IDs to objects
      // so downstream code can assume a consistent shape
      const normalizedElements = normalizeDashboardElements(elements);

      const currentState = get();
      const currentElements = currentState.dashboardElements;

      const elementsEqual =
        currentElements.length === normalizedElements.length &&
        currentElements.every((existing, index) => {
          const next = normalizedElements[index];
          if (typeof existing === "object" && typeof next === "object") {
            return JSON.stringify(existing) === JSON.stringify(next);
          }
          return false;
        });

      if (elementsEqual) {
        return;
      }

      // Clone elements and assign IDs to guide tiles that don't have them
      const clonedDashboardElements = normalizedElements.map((element) => {
        if (typeof element === "object" && "headerTitle" in element) {
          const guideTile = element as guide_tile_config;
          // If guide tile doesn't have an ID, assign one
          if (!("id" in guideTile) || !guideTile.id) {
            const newId = `guide-tile-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            return { ...guideTile, id: newId };
          }
          return { ...guideTile };
        }
        return element;
      }) as dashboard_element[];

      const clonedOriginalElements = clonedDashboardElements.map((element) =>
        typeof element === "object" ? { ...element } : element
      ) as dashboard_element[];

      set((state) => ({
        ...state,
        dashboardElements: clonedDashboardElements,
        originalDashboardElements: clonedOriginalElements,
        history: [
          clonedOriginalElements.map((element) =>
            typeof element === "object" ? { ...element } : element
          ),
        ],
        historyIndex: 0,
      }));
    },
    setDashboardElements: (updater, options) => {
      set((state) => {
        const nextValue =
          typeof updater === "function"
            ? (updater as (prev: dashboard_element[]) => dashboard_element[])(
                state.dashboardElements
              )
            : updater;

        const shouldRecord = options?.recordHistory ?? true;

        let history = state.history;
        let historyIndex = state.historyIndex;

        const elementsEqual =
          state.dashboardElements.length === nextValue.length &&
          state.dashboardElements.every((existing, index) => {
            const next = nextValue[index];
            if (typeof existing === "string" && typeof next === "string") {
              return existing === next;
            }
            if (typeof existing === "object" && typeof next === "object") {
              return JSON.stringify(existing) === JSON.stringify(next);
            }
            return false;
          });

        if (elementsEqual) {
          return state;
        }

        if (shouldRecord) {
          const trimmedHistory = history.slice(0, historyIndex + 1);
          const snapshot = nextValue.map((element) =>
            typeof element === "object" ? { ...element } : element
          );
          history = [...trimmedHistory, snapshot];
          historyIndex = history.length - 1;
        }

        return {
          ...state,
          dashboardElements: nextValue,
          history,
          historyIndex,
        };
      });
    },
    resetDashboardElements: () => {
      const state = get();
      set((prev) => ({
        ...prev,
        dashboardElements: state.originalDashboardElements.map((element) =>
          typeof element === "object" ? { ...element } : element
        ) as dashboard_element[],
        history: [
          state.originalDashboardElements.map((element) =>
            typeof element === "object" ? { ...element } : element
          ),
        ],
        historyIndex: 0,
      }));
    },
    commitDashboardElements: () => {
      const state = get();
      set((prev) => ({
        ...prev,
        originalDashboardElements: state.dashboardElements.map((element) =>
          typeof element === "object" ? { ...element } : element
        ) as dashboard_element[],
        history: [
          state.dashboardElements.map((element) =>
            typeof element === "object" ? { ...element } : element
          ),
        ],
        historyIndex: 0,
      }));
    },
    setGuideTiles: (updater) => {
      set((state) => ({
        ...state,
        guideTiles:
          typeof updater === "function"
            ? (
                updater as (
                  prev: edit_guide_tile_config[]
                ) => edit_guide_tile_config[]
              )(state.guideTiles)
            : updater,
      }));
    },
    reindexGuideTiles: () => {
      set((state) => ({
        ...state,
        guideTiles: state.guideTiles.map((tile, idx) => ({
          ...tile,
          index: idx,
        })),
      }));
    },
    reorderGuideTilesByElements: (elements) => {
      set((state) => {
        const reordered: edit_guide_tile_config[] = [];
        let idx = 0;
        elements.forEach((el) => {
          if (typeof el === "object" && "headerTitle" in el) {
            const guideTile = el as guide_tile_config;
            const elementId = guideTile.id;
            // Match by persistent ID first, fallback to content matching for legacy tiles
            const match = state.guideTiles.find((t) => {
              if (elementId && t.id === elementId) {
                return true;
              }
              // Fallback: match by content for tiles without IDs
              const elCfgs =
                guideTile.displayConfigs?.map((c) =>
                  typeof c === "string" ? c : c.display_id
                ) || [];
              const tCfgs = t.displayConfigs.map((c) =>
                typeof c === "string" ? c : c.display_id
              );
              if (tCfgs.length !== elCfgs.length) return false;
              for (let i = 0; i < tCfgs.length; i++)
                if (tCfgs[i] !== elCfgs[i]) return false;
              return true;
            });
            if (match) {
              // Preserve the element's current displayConfigs (which may have been reordered)
              reordered.push({
                ...match,
                index: idx,
                displayConfigs:
                  guideTile.displayConfigs || match.displayConfigs,
              });
            } else {
              const displayConfigs = guideTile.displayConfigs || [];
              reordered.push({
                id:
                  elementId ||
                  `guide-tile-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                index: idx,
                headerTitle: guideTile.headerTitle ?? "",
                headerText: guideTile.headerText ?? "",
                displayConfigs: displayConfigs,
                footerTitle: guideTile.footerTitle ?? "",
                footerText: guideTile.footerText ?? "",
                backgroundColor: guideTile.backgroundColor,
                textBackgroundColor: guideTile.textBackgroundColor,
                textColor: guideTile.textColor,
                textDisplayMode: guideTile.textDisplayMode,
              });
            }
            idx += 1;
          }
        });

        return { ...state, guideTiles: reordered };
      });
    },
    setCurrentDashboardDisplayIds: (updater) => {
      set((state) => ({
        ...state,
        currentDashboardDisplayIds:
          typeof updater === "function"
            ? (updater as (prev: string[]) => string[])(
                state.currentDashboardDisplayIds
              )
            : updater,
      }));
    },
    addDisplayIdToCurrentDashboard: (id) => {
      const trimmed = id.trim();
      if (!trimmed) {
        return;
      }

      const state = get();
      if (state.currentDashboardDisplayIds.includes(trimmed)) {
        return;
      }

      set((prev) => ({
        ...prev,
        currentDashboardDisplayIds: [
          ...state.currentDashboardDisplayIds,
          trimmed,
        ],
      }));
    },
    setInsertionTarget: (target) => {
      set((state) => ({ ...state, insertionTarget: target }));
    },
    insertGaugeAtTarget: (displayId) => {
      const trimmed = displayId.trim();
      const state = get();
      const target = state.insertionTarget;
      if (!trimmed || !target) {
        return "no-target";
      }

      // Only reject duplicates that already sit at the dashboard top level.
      // A gauge nested inside a guide tile is fine to add again standalone —
      // the two live in different places, so it's not really a duplicate.
      const existsAtTopLevel = state.dashboardElements.some((el) => {
        if (typeof el === "object" && "display_id" in el) {
          return (el as display_config).display_id === trimmed;
        }
        return typeof el === "string" && el === trimmed;
      });
      if (existsAtTopLevel) {
        return "duplicate";
      }

      const newConfig: display_config = {
        display_id: trimmed,
        view_name: "rawlevel",
      };

      // Top-level insert at the requested index
      state.setDashboardElements((prev) => {
        const next = [...prev];
        next.splice(Math.max(0, Math.min(target.index, next.length)), 0, newConfig);
        return next;
      });
      set((prev) => ({
        ...prev,
        // Track the id so the search "already added" check and the
        // add-from-search effect stay consistent with the elements.
        currentDashboardDisplayIds: prev.currentDashboardDisplayIds.includes(
          trimmed
        )
          ? prev.currentDashboardDisplayIds
          : [...prev.currentDashboardDisplayIds, trimmed],
        // Advance the anchor so consecutive picks land in pick order.
        insertionTarget: { kind: "topLevel", index: target.index + 1 },
      }));
      return "inserted";
    },
    insertContentTileAt: (tile, index) => {
      get().setDashboardElements((prev) => {
        const next = [...prev];
        next.splice(Math.max(0, Math.min(index, next.length)), 0, tile);
        return next;
      });
    },
    toggleTileSelection: (id) => {
      set((state) => {
        const exists = state.selectedTiles.includes(id);
        return {
          ...state,
          selectedTiles: exists
            ? state.selectedTiles.filter((item) => item !== id)
            : [...state.selectedTiles, id],
        };
      });
    },
    setTileSelection: (ids) => {
      set((state) => ({
        ...state,
        selectedTiles: Array.from(
          new Set(ids.map((id) => id.trim()).filter(Boolean))
        ),
      }));
    },
    clearTileSelection: () => {
      set((state) => ({
        ...state,
        selectedTiles: [],
      }));
    },
    setGuideTileEditingState: (guideTileId, isEditing) => {
      set((state) => {
        const exists = state.editingGuideTileIds.includes(guideTileId);
        if (isEditing && !exists) {
          return {
            ...state,
            editingGuideTileIds: [...state.editingGuideTileIds, guideTileId],
          };
        }
        if (!isEditing && exists) {
          return {
            ...state,
            editingGuideTileIds: state.editingGuideTileIds.filter(
              (id) => id !== guideTileId
            ),
          };
        }
        return state;
      });
    },
    clearGuideTileEditingState: () => {
      set((state) => ({
        ...state,
        editingGuideTileIds: [],
      }));
    },
    undo: () => {
      const state = get();
      if (state.historyIndex <= 0) {
        return;
      }
      const newIndex = state.historyIndex - 1;
      const snapshot = state.history[newIndex] || [];
      set((prev) => ({
        ...prev,
        dashboardElements: snapshot.map((element) =>
          typeof element === "object" ? { ...element } : element
        ),
        historyIndex: newIndex,
      }));
    },
    redo: () => {
      const state = get();
      if (state.historyIndex >= state.history.length - 1) {
        return;
      }
      const newIndex = state.historyIndex + 1;
      const snapshot = state.history[newIndex] || [];
      set((prev) => ({
        ...prev,
        dashboardElements: snapshot.map((element) =>
          typeof element === "object" ? { ...element } : element
        ),
        historyIndex: newIndex,
      }));
    },
    resetEditorState: () => {
      const state = get();
      set(() => ({
        ...state,
        isEditMode: false,
        nameEdited: false,
        guideTiles: [],
        currentDashboardDisplayIds: [],
        dashboardElements: state.originalDashboardElements.map((element) =>
          typeof element === "object" ? { ...element } : element
        ) as dashboard_element[],
        selectedTiles: [],
        editingGuideTileIds: [],
        insertionTarget: null,
        history: [
          state.originalDashboardElements.map((element) =>
            typeof element === "object" ? { ...element } : element
          ),
        ],
        historyIndex: 0,
      }));
    },
    updateElementConfig: (index, config) => {
      set((state) => {
        const newElements = [...state.dashboardElements];
        if (index >= 0 && index < newElements.length) {
          newElements[index] = config;
        }

        const history = state.history.slice(0, state.historyIndex + 1);
        const snapshot = newElements.map((element) =>
          typeof element === "object" ? { ...element } : element
        );

        return {
          ...state,
          dashboardElements: newElements,
          history: [...history, snapshot],
          historyIndex: history.length,
        };
      });
    },
  })
);

/**
 * Whether leaving the dashboard now would lose edits: it's in edit mode and
 * its tiles or name differ from what's saved. Entering edit mode without
 * changing anything doesn't count.
 */
export function hasUnsavedDashboardEdits(state: DashboardEditorState): boolean {
  if (!state.isEditMode) return false;
  if (state.nameEdited) return true;
  return JSON.stringify(state.dashboardElements) !== JSON.stringify(state.originalDashboardElements);
}
